import { useEffect, useRef, useState } from "react";
import { ConfigProvider, Button, Form, Input, Select, Modal, Row, Spin, Col, Empty, Pagination, message } from "antd";
import type { PaginationProps } from "antd";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { publicPath } from "@/utils/base";
import { appNameReg } from "@/utils/constant";
import { getDevicePluginDetail } from "@/utils";
import api from "@/api";
import apiStaging from "@/api/staging";
import SelectWithAll from "@/components/SelectWithAll";
import "./index.less";

import dataJson from "@/../public/data.json";
import { cloneDeep } from "lodash-es";

const prefix = "myDevicePlugin";
function MyDevicePlugin() {
  const { my_ai_model, req_err, create, two_bars, app_detail, release, add_create } = dataJson;
  const filterFormJson = my_ai_model.filter_form;
  const addFormJson = my_ai_model.add_form;

  const [addOpen, setAddOpen] = useState(false);
  const [addOpenLoading, setAddFormLoading] = useState(false);
  const [pageData, setPageData] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  const [filterForm] = Form.useForm();

  const [addForm] = Form.useForm();

  const navigate = useNavigate();
  const userData = useSelector((state: any) => state.userData);

  const pageInit = {
    pageSize: 20,
    pageNo: 1,
  };
  const pageNo = useRef<number>(pageInit.pageNo);
  const pagesize = useRef<number>(pageInit.pageSize);

  const onFilter = () => {
    pageNo.current = pageInit.pageNo;
    pagesize.current = pageInit.pageSize;
    getPageData();
  };

  const onShowSizeChange: PaginationProps["onShowSizeChange"] = (current, pageSize) => {
    pagesize.current = pageSize;
  };

  const getPageData = async () => {
    try {
      setLoading(true);
      const filterFormValues = await filterForm.getFieldsValue();
      const res: any = await api.getDevicePluginPage({
        page: pageNo.current,
        limit: pagesize.current,
        keyword: filterFormValues.name,
        typeKeys: filterFormValues.type,
        showModes: filterFormValues.status,
      });
      if (res.code === 200) {
        const dataCopy = cloneDeep(res.data.datas);
        const list = dataCopy.map((item: any) => {
          item.iconImg = item?.resources?.find((ele: any) => ele.type == "ICON")?.url;
          const showModeItem = release.show_mode_arr?.find((ele: any) => ele.value === item.showMode);
          item.title = showModeItem?.title;
          item.textColor = showModeItem?.textColor;
          item.bgColor = showModeItem?.bgColor;
          return item;
        });
        setPageData(list);
        setTotal(Number(res?.data?.total));
        setLoading(false);
      } else {
        setLoading(false);
        message.error(res.msg || req_err);
      }
    } catch (errorInfo: any) {
      setPageData([]);
      setTotal(0);
      setLoading(false);
    }
  };

  const validateName = (_: any, value: any) => {
    if (value) {
      return apiStaging.allRepeatName({ name: value }).then((resp: any) => {
        if (resp.code === 200) {
          if (resp.data?.exist) {
            return Promise.reject(my_ai_model.name_already);
          }
        } else {
          return Promise.resolve();
        }
      });
    } else {
      return Promise.resolve();
    }
  };

  const showAddModal = () => {
    setAddOpen(true);
  };

  const handleAddOpenOk = async () => {
    try {
      const addValues = await addForm.validateFields();
      setAddFormLoading(true);
      const res: any = await api.createDevicePlugin({
        name: addValues.name,
        subType: ["SO"],
      });
      if (res.code === 200) {
        message.success(my_ai_model.creat_success);
        setAddOpen(false);
        navigate(`${publicPath}/my_device_plugin_detail/info/${res.data.id}`);
      } else {
        setAddFormLoading(false);
        message.error(res.msg || req_err);
      }
    } catch (errorInfo: any) {
      setAddFormLoading(false);
    }
  };

  const handleAddCancel = () => {
    setAddOpen(false);
  };

  const clickCard = (item: any) => {
    getDevicePluginDetail(item.id, () => {
      navigate(`${publicPath}/my_device_plugin_detail/info/${item.id}`);
    });
  };

  useEffect(() => {
    if (JSON.stringify(userData) !== "{}") {
      getPageData();
    }
  }, [userData]);

  return (
    <div className={prefix}>
      <div className={`${prefix}-top`}>
        <Form
          form={filterForm}
          layout="inline"
          initialValues={{
            status: filterFormJson.status_options.map((item) => item.value),
          }}
        >
          <Form.Item
            name="name"
            getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
            style={{ marginRight: 56 }}
          >
            <Input placeholder={filterFormJson.name_placeholder} onPressEnter={onFilter} style={{ width: 296 }} />
          </Form.Item>
          <Form.Item name="status" label={filterFormJson.status_label}>
            {/* <Select
              allowClear
              mode="multiple"
              maxTagCount="responsive"
              options={filterFormJson.status_options}
              placeholder={filterFormJson.status_placeholder}
              onChange={onFilter}
              style={{ width: 296 }}
              defaultValue={""}
            /> */}
            <SelectWithAll
              options={filterFormJson.status_options}
              placeholder={filterFormJson.status_placeholder}
              onChange={onFilter}
              style={{ width: 296 }}
              value={filterForm.getFieldValue("status")}
            />
          </Form.Item>
        </Form>
        <ConfigProvider
          theme={{
            components: {
              Button: {
                borderRadius: 0,
                paddingInline: 40,
                paddingBlock: 8,
              },
            },
          }}
        >
          <Button type="primary" onClick={showAddModal}>
            {create}
          </Button>
        </ConfigProvider>
      </div>
      <Spin spinning={loading} size="large" wrapperClassName={`${prefix}-spin`}>
        {total === 0 ? (
          <Empty />
        ) : (
          <div className={`${prefix}-content`}>
            {pageData.map((item: any, index: number) => {
              return (
                <div
                  key={index}
                  onClick={() => {
                    clickCard(item);
                  }}
                  className={`${prefix}-content-item`}
                >
                  <div
                    className={`${prefix}-content-status`}
                    style={{ color: item?.textColor, backgroundColor: item?.bgColor }}
                  >
                    {item.title}
                  </div>
                  <div className={`${prefix}-content-inner`}>
                    <img src={item?.iconImg ? item?.iconImg : my_ai_model.init_img} alt="" />
                    <div>
                      <div title={item?.name} className={`${prefix}-content-title one-line-ellipsis`}>
                        {item?.name || two_bars}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Spin>
      <Pagination
        pageSize={pagesize.current}
        current={pageNo.current}
        total={total}
        showSizeChanger
        showQuickJumper
        showTotal={(total) => `共 ${total} 条`}
        align="end"
        className={`${prefix}_page`}
        onShowSizeChange={onShowSizeChange}
        onChange={(page: number) => {
          pageNo.current = page;
          getPageData();
        }}
      />
      <Modal
        title={create}
        centered
        destroyOnClose
        confirmLoading={addOpenLoading}
        open={addOpen}
        okText={add_create}
        onOk={handleAddOpenOk}
        onCancel={handleAddCancel}
        width={328}
      >
        <Form form={addForm} layout="vertical" clearOnDestroy>
          <Form.Item
            name="name"
            label={addFormJson.name_label}
            validateDebounce={500}
            rules={[
              { required: true, message: addFormJson.name_placeholder },
              {
                pattern: appNameReg,
                message: app_detail.name_validator,
              },
              { validator: validateName },
            ]}
            getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
          >
            <Input showCount maxLength={30} placeholder={addFormJson.name_placeholder} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default MyDevicePlugin;
